import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { logout } from '../redux/slices/authSlice';
import { clearAllClassData } from '../redux/slices/classSlice';
import { clearCampData } from '../redux/slices/campSlice';
import { clearCenter } from '../redux/slices/centerSlice';
import { clearCenters } from '../redux/slices/centersListSlice';
import Login from './Login';

const UserProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [showLogin, setShowLogin] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const handleLoginClick = () => {
    setShowLogin(true);
  };

  const handleLoginSuccess = () => {
    setShowLogin(false);
  };

  const handleLoginCancel = () => {
    setShowLogin(false);
  };

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearAllClassData());
    dispatch(clearCampData());
    dispatch(clearCenter());
    dispatch(clearCenters());
    sessionStorage.removeItem('yk_camp_centers_cache');
    sessionStorage.removeItem('yk_class_centers_cache');
    setShowDropdown(false);
    navigate('/');
  };

  if (!isAuthenticated) {
    return (
      <>
        <button
          onClick={handleLoginClick}
          className="flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-2 px-4 rounded-full border-2 border-gray-200 transition shadow-sm hover:shadow-md text-sm"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
          Login
        </button>

        {showLogin && (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onCancel={handleLoginCancel}
            entity="RMS"
          />
        )}
      </>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 hover:bg-gray-50 transition-colors duration-200"
      >
        <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-sm shadow-md hover:shadow-lg transition-shadow duration-200">
          {(() => {
            const fn = user?.User_First_Name || user?.user_First_Name || user?.firstName || '';
            const ln = user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '';
            if (fn) return (fn.charAt(0) + (ln ? ln.charAt(0) : '')).toUpperCase();
            return user?.email ? user.email.split('@')[0].split('.').map(p => p.charAt(0).toUpperCase()).join('') : 'U';
          })()}
        </div>
        <svg 
          className={`w-4 h-4 text-gray-600 transition-transform ${showDropdown ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-30" 
            onClick={() => setShowDropdown(false)}
          ></div>

          {/* Dropdown Menu */}
          <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border-2 border-gray-100 z-40 overflow-hidden animate-slideInRight">
            {/* User Info */}
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 border-b-2 border-gray-100">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  {(() => {
                    const fn = user?.User_First_Name || user?.user_First_Name || user?.firstName || '';
                    const ln = user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '';
                    if (fn) return (fn.charAt(0) + (ln ? ln.charAt(0) : '')).toUpperCase();
                    return user?.email ? user.email.split('@')[0].split('.').map(p => p.charAt(0).toUpperCase()).join('') : 'U';
                  })()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-gray-900 text-sm break-words">
                    {(() => {
                      const fn = user?.User_First_Name || user?.user_First_Name || user?.firstName || '';
                      const ln = user?.User_Last_Name || user?.user_Last_Name || user?.lastName || '';
                      if (fn || ln) return `${fn} ${ln}`.trim();
                      if (user?.name) return user.name;
                      return user?.email?.split('@')[0].split('.').map(p => p.charAt(0).toUpperCase() + p.slice(1)).join(' ') || '';
                    })()}
                  </p>
                  <p className="text-xs text-gray-600 break-all">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Menu Items */}
            <div className="py-2">
              <button
                onClick={() => { setShowDropdown(false); navigate('/profile'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-blue-50 transition text-gray-700 font-medium"
              >
                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Edit Profile
              </button>
              <button
                onClick={() => { setShowDropdown(false); navigate('/orders'); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-purple-50 transition text-gray-700 font-medium"
              >
                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                My Orders
              </button>
              <div className="border-t border-gray-100 mx-3 my-1"></div>
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-red-50 transition text-red-600 font-semibold"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default UserProfile;
